import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@/shared/i18n';
import { Icon } from '@/shared/ui/icon';
import { WorkerizeMark } from '@/shared/ui/brand';
import { ROUTES } from '@/app/router/route-paths';
import { AppLocaleToggle } from './AppLocaleToggle';
import { AppSidebar } from './AppSidebar';
import { AppUserMenu } from './AppUserMenu';

/**
 * Mobile navigation drawer — the small-screen stand-in for the desktop Ink
 * rail. Shares the dark Ink surface so `AppSidebar`'s on-ink tokens read with
 * full contrast, and renders the navigation in its labelled (expanded) form.
 */
export function AppMobileDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactNode {
  const { t } = useT();

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      <button
        type="button"
        aria-label={t('appShell.drawer.close')}
        onClick={onClose}
        className="absolute inset-0 bg-[var(--brand-ink)]/50"
      />
      <aside className="absolute inset-y-0 start-0 flex w-[min(82vw,18rem)] flex-col border-e border-[var(--ink-band-border)] bg-[var(--ink-band)] p-4 text-[var(--on-ink-band)] shadow-panel">
        <div className="mb-6 flex items-center justify-between gap-4">
          <Link
            to={ROUTES.app.root}
            onClick={onClose}
            aria-label={t('app.name')}
            className="inline-flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60"
          >
            <WorkerizeMark size={26} />
            <span className="text-lg font-semibold tracking-tight text-[var(--on-ink-band)]">
              {t('app.name')}
            </span>
          </Link>
          <button
            type="button"
            aria-label={t('appShell.drawer.close')}
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[var(--ink-band-border)] text-[var(--on-ink-band-variant)] transition-colors hover:bg-[var(--surface-overlay)] hover:text-[var(--on-ink-band)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <AppSidebar onNavigate={onClose} />
        </div>

        <div className="mt-4 flex items-center gap-3 border-t border-[var(--ink-band-border)] pt-4">
          <AppUserMenu />
          <AppLocaleToggle onInk />
        </div>
      </aside>
    </div>
  );
}
